import axios from 'axios'
import { toast } from 'sonner'
import { resolveApiBase } from './env'
import {
  TERMINAL_LOGOUT_CODES,
  classifyRefreshError,
  classifyRefreshResponse,
} from './session-refresh'

const baseURL = resolveApiBase()
// Exposed so the Google SSO "Continue with Google" button can build an
// absolute start URL that points at the API (same-origin or api-subdomain).
export const apiBaseUrl = baseURL

/**
 * Both auth tokens live in httpOnly cookies set by the API:
 *   - taskmagix_access  (path /, lifetime = JWT_ACCESS_EXPIRY — path is
 *     "/" so the same cookie also rides the Socket.IO handshake)
 *   - taskmagix_refresh (path /api/v1/auth, lifetime = JWT_REFRESH_EXPIRY)
 *
 * `withCredentials: true` causes the browser to send them on every
 * cross-origin call. There is no in-memory token to manage and no
 * Authorization header to set — XSS payloads can't read or replay
 * these cookies.
 */
export const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
})

let refreshPromise = null

/**
 * Public, deduped session-refresh primitive. Used by:
 *   - the HTTP response interceptor below, which retries the original
 *     request on a 401, and
 *   - the Socket.IO `connect_error` handler in `lib/socket.js`, which
 *     reconnects after the access cookie expires.
 *
 * Concurrent callers share a single in-flight `/auth/refresh` POST
 * (the `refreshPromise` slot). This matters during the access-expiry
 * cliff: a fresh page can fire half a dozen GETs in quick succession
 * AND the socket can `connect_error` at the same time — we only want
 * ONE refresh call out on the wire, not seven.
 *
 * Returns `{ ok, terminalError, noSession }` exactly like `performRefresh`
 * (shaping lives in lib/session-refresh.js):
 *   - `ok: true`  → fresh access cookie is on the browser, caller
 *                   should retry whatever it was doing
 *   - `terminalError: { code, message }` → session is dead, caller
 *                   should dispatch force-logout
 *   - `noSession: true` → the browser holds no session cookies at all;
 *                   retrying cannot help
 *   - all falsy → transient failure (network, 429), leave the user
 *                   signed in but tell them to retry
 */
export function refreshSession() {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null
  })
  return refreshPromise
}

async function performRefresh() {
  try {
    const res = await axios.post(
      `${baseURL}/auth/refresh`,
      {},
      { withCredentials: true, timeout: 15_000 },
    )
    // `200 { session: false }` → `noSession` (see lib/session-refresh.js).
    return classifyRefreshResponse(res?.data)
  } catch (err) {
    // Terminal code → force-logout; 429 / network → transient; any other
    // HTTP failure → synthesised `session_expired` (lib/session-refresh.js).
    return classifyRefreshError(err)
  }
}

/** Dispatch the global "force logout" event. The auth store listens and
 *  handles toast + state reset. Defined at module scope so we can fire it
 *  from both the response interceptor and the socket layer without an
 *  import cycle.
 *
 *  `opts.local` — tear down THIS tab only, without the best-effort
 *  `/auth/sign-out` call, and only if the tab is currently signed in.
 *  Used when the browser already holds no session cookies: there is
 *  nothing to sign out server-side, and a late sign-out POST could kill a
 *  session another tab has just started. */
function dispatchForceLogout(reason, message, opts = {}) {
  try {
    window.dispatchEvent(
      new CustomEvent('taskmagix:force-logout', {
        detail: { reason, message, local: Boolean(opts.local) },
      }),
    )
  } catch {
    /* SSR / non-browser env — no-op */
  }
}

/**
 * Plan-limit detector — true when the API responded with our
 * structured `plan_limit_reached` error from billing/limits.js.
 * Exported so individual mutation handlers can suppress their
 * generic "could not save" toast in favour of the more specific
 * upgrade message that the response interceptor surfaces.
 */
export function isPlanLimitError(err) {
  if (!axios.isAxiosError(err)) return false
  return err.response?.data?.error?.code === 'plan_limit_reached'
}

/**
 * True when the failure means the SESSION itself has ended.
 *
 * By the time a 401 reaches a mutation handler the interceptor has
 * already tried to refresh and retry; anything still failing is a dead
 * session, and the interceptor has dispatched force-logout — which shows
 * its own toast and sends the user to sign-in. Handlers must therefore
 * skip their generic error toast for these, exactly as they do for
 * `isPlanLimitError`. Without it the user gets the SAME sentence twice:
 * once from force-logout, once from the handler (QA hit this as two
 * stacked "Authentication required." toasts).
 */
export function isSessionEndedError(err) {
  if (!axios.isAxiosError(err)) return false
  if (err.response?.status === 401) return true
  return TERMINAL_LOGOUT_CODES.has(err.response?.data?.error?.code)
}

/**
 * Maintenance-mode detector — true when the API responded with our
 * structured `{ code: 'maintenance' }` 503 from the auth pipeline
 * (middleware/auth.js). Exported so mutation handlers and page
 * `isError` branches can:
 *   - Skip their own "could not save" toast (the response
 *     interceptor already dispatched the maintenance-hit event,
 *     and the MaintenanceGate redirect to /maintenance is imminent
 *     on the next render tick).
 *   - Render a quiet "Redirecting…" placeholder instead of a noisy
 *     error card.
 *
 * Pairs symmetrically with `isPlanLimitError` above.
 */
export function isMaintenanceError(err) {
  if (!axios.isAxiosError(err)) return false
  const code = err.response?.data?.error?.code
  const status = err.response?.status
  return code === 'maintenance' || (status === 503 && code === 'maintenance')
}

/** De-dupe hash so a burst of failed calls (e.g. four list mutations
 *  fired in parallel) doesn't stack four identical toasts on top of
 *  each other. Cleared 4 s after the toast fires. */
let planLimitToastFiredAt = 0
function maybeFirePlanLimitToast(message) {
  const now = Date.now()
  if (now - planLimitToastFiredAt < 4_000) return
  planLimitToastFiredAt = now
  toast.warning(
    message ??
      'Your current plan limit has been reached. Please upgrade your plan from the Billing page to continue.',
  )
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config
    const url = original?.url ?? ''

    // Never try to refresh on auth endpoints themselves.
    const isAuthEndpoint =
      url.includes('/auth/sign-in') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh') ||
      url.includes('/auth/sign-out')

    // Terminal code on the failing call itself — e.g. a 403
    // user_deactivated or tenant_suspended from the auth middleware. No
    // refresh will fix it; force a logout right away.
    //
    // Critically, skip this path when the failing call IS an auth
    // endpoint. The SPA bootstrap fires `/auth/refresh` on every page
    // load to detect an existing session — for fresh visitors with no
    // cookies, the server now returns `session_expired` (terminal).
    // Without this guard, every signed-out visitor would see a
    // misleading "Your session has expired" toast on landing. The
    // bootstrap / sign-in / sign-out callers each handle their own
    // teardown — they don't need (and don't want) the global toast.
    // The refresh-retry path below still dispatches force-logout
    // when a normal endpoint's 401 → refresh chain ends in failure,
    // which is the case we actually care about.
    const code = error.response?.data?.error?.code
    if (TERMINAL_LOGOUT_CODES.has(code) && !isAuthEndpoint) {
      dispatchForceLogout(code, error.response?.data?.error?.message)
      return Promise.reject(error)
    }

    // Plan-cap hits — surface a single global warning toast and let
    // the call site reject normally so React Query can flip its
    // `isError` flag.
    if (code === 'plan_limit_reached') {
      maybeFirePlanLimitToast(error.response?.data?.error?.message)
      return Promise.reject(error)
    }

    // Maintenance mode — the backend's auth pipeline 503s every
    // authenticated request from a user whose panel is in maintenance.
    // Notify the maintenance store via a window event so the affected
    // tab flips into the maintenance screen without waiting for the
    // next /status poll. Custom event keeps lib/api.js free of any
    // store imports that would create a cycle through socket.js.
    if (error.response?.status === 503 && code === 'maintenance') {
      try {
        window.dispatchEvent(
          new CustomEvent('taskmagix:maintenance-hit', {
            detail: {
              scope: error.response?.data?.error?.scope ?? null,
              message: error.response?.data?.error?.message ?? null,
            },
          }),
        )
      } catch {
        /* SSR / non-browser env — no-op */
      }
      return Promise.reject(error)
    }

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !isAuthEndpoint
    ) {
      original._retry = true
      // Shared with the socket layer — only one `/auth/refresh` POST
      // ever in flight at a time even when HTTP + socket both notice
      // the cookie has expired in the same moment.
      const result = await refreshSession()
      if (result.ok) {
        // The new access cookie is already on the browser; just retry —
        // axios picks it up automatically thanks to withCredentials.
        return api(original)
      }
      if (result.terminalError) {
        dispatchForceLogout(result.terminalError.code, result.terminalError.message)
      } else if (result.noSession) {
        // [QA-04] The browser holds no session cookies any more (signed out
        // in another tab, or both cookies lapsed). Leaving the signed-in
        // chrome up would keep rendering a session that no longer exists,
        // so end it locally. `local` → no server sign-out call, and a no-op
        // for a tab that isn't signed in (a fresh visitor's bootstrap 401
        // lands here too).
        dispatchForceLogout(
          'session_expired',
          'Your session has expired. Please sign in again.',
          { local: true },
        )
      }
    }
    return Promise.reject(error)
  },
)

export { dispatchForceLogout }

/**
 * Friendly fallback messages keyed by HTTP status. Used only when the
 * backend didn't ship a structured error body of its own (e.g. when
 * an upstream proxy returns 502/504 before the request ever reaches
 * our API, or when the server crashes mid-response).
 *
 * Phrasing target: tell the user *what to do next*, not what code
 * came back. "Request failed with status code 502" is technically
 * accurate and operationally useless — "The server is temporarily
 * unavailable. Please try again in a moment." gives the user a path.
 */
const FRIENDLY_BY_STATUS = {
  400: 'That request wasn\'t accepted. Check the form and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You don\'t have permission to do that.',
  404: 'We couldn\'t find what you were looking for.',
  408: 'The server took too long to respond. Please try again.',
  409: 'That conflicts with something that already exists.',
  413: 'That file is too large to upload.',
  415: 'That file type isn\'t supported.',
  422: 'Some of the details you provided aren\'t valid.',
  429: 'You\'re going a bit fast — please wait a moment and try again.',
  500: 'Something went wrong on the server. Please try again in a moment.',
  502: 'The server is temporarily unavailable. Please try again in a moment.',
  503: 'The server is temporarily unavailable. Please try again in a moment.',
  504: 'The server took too long to respond. Please try again.',
}

/** Raw axios error messages we should NEVER surface to the user —
 *  they leak HTTP plumbing ("Request failed with status code 502")
 *  or low-level network noise ("Network Error", "timeout of 30000ms
 *  exceeded") that doesn't help anyone. When we see one of these,
 *  prefer the status-based friendly message instead. */
const RAW_AXIOS_PATTERNS = [
  /^Request failed with status code \d+/i,
  /^Network Error$/i,
  /^timeout of \d+ms exceeded/i,
  /^socket hang up$/i,
]

function isRawAxiosMessage(msg) {
  if (!msg) return true
  return RAW_AXIOS_PATTERNS.some((re) => re.test(String(msg).trim()))
}

/**
 * Always returns `{ message: string, fields: object|null }`. The
 * `fields` key is intentionally always present (null when there are
 * no validation errors) so the ~35 call sites doing
 * `const { message, fields } = extractApiError(err)` and then
 * `fields?.someKey` or `if (fields)` don't crash on the destructure
 * for non-validation errors. Previously `.fields` was only set on
 * the validation branch, so a 5xx that arrived at one of those call
 * sites would leave `fields` undefined and any property access on
 * it would throw — exactly the silent regression we want to avoid.
 */
export function extractApiError(err) {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data
    const status = err.response?.status
    const code = body?.error?.code

    // 0) Maintenance mode — the auth pipeline returns 503 with
    //    `code: 'maintenance'` on every authenticated request from a
    //    panel that's currently in maintenance. The
    //    `taskmagix:maintenance-hit` event the response interceptor
    //    dispatches (lib/api.js above) will flip the SPA over to the
    //    /maintenance screen within the next render tick — but during
    //    that tick the failing page's `isError` branch renders the
    //    extractApiError message. Return a friendlier transient
    //    string so users never see "Something went wrong" while the
    //    redirect is in flight. The actual maintenance copy (the one
    //    the Super Admin typed in the Maintenance settings) is shown
    //    on the destination screen.
    if (code === 'maintenance' || (status === 503 && code === 'maintenance')) {
      return {
        message:
          body?.error?.message ??
          'Maintenance in progress — taking you to the status page…',
        fields: null,
      }
    }

    // 1) Structured validation errors — preferred: backend already
    //    knows which fields failed and why.
    if (body?.error?.fields) {
      return {
        message: body.error.message ?? 'Validation failed.',
        fields: body.error.fields,
      }
    }

    // 2) Backend's own message — preferred when it's a real one. We
    //    deliberately drop straight to the status-based fallback when
    //    there's no response body at all (502/504 from an upstream
    //    proxy never give us a JSON body to read).
    //
    //    Exception: a GENERIC 5xx body — the API's default
    //    `{ message: 'Internal server error.' }` — is exactly the
    //    unhelpful phrasing FRIENDLY_BY_STATUS exists to replace, so let
    //    it fall through to the friendly status message below
    //    ("Something went wrong on the server. Please try again in a
    //    moment.") instead of surfacing "Internal server error." to the
    //    user. Specific backend messages (incl. non-generic 5xx) still win.
    const backendMsg = body?.error?.message ?? body?.message ?? null
    const isGeneric5xx =
      typeof status === 'number' &&
      status >= 500 &&
      /^internal server error\.?$/i.test(String(backendMsg ?? '').trim())
    if (backendMsg && !isGeneric5xx) {
      const trimmed = String(backendMsg).trim()
      return {
        message: trimmed.endsWith('.') ? trimmed : `${trimmed}.`,
        fields: null,
      }
    }

    // 3) Status-based friendly fallback — covers 502 / 504 / network
    //    errors where there's no body and the raw axios message is
    //    "Request failed with status code N".
    if (status && FRIENDLY_BY_STATUS[status]) {
      return { message: FRIENDLY_BY_STATUS[status], fields: null }
    }

    // 4) Network failure (no response at all — DNS, refused
    //    connection, request never left the browser).
    if (!err.response) {
      return {
        message:
          'Can\'t reach the server. Check your connection and try again.',
        fields: null,
      }
    }

    // 5) Last resort — use err.message only if it's not raw axios
    //    plumbing; otherwise return a generic.
    if (!isRawAxiosMessage(err.message)) {
      const trimmed = String(err.message).trim()
      return {
        message: trimmed.endsWith('.') ? trimmed : `${trimmed}.`,
        fields: null,
      }
    }
    return {
      message: 'Something went wrong. Please try again.',
      fields: null,
    }
  }
  return {
    message: 'Something went wrong. Please try again.',
    fields: null,
  }
}
